import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Briefcase,
  Trash2,
  Plus,
  ExternalLink,
  Loader2,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useProject, useUpdateProject, useUpdateProjectStatus, useRemoveJobFromProject, useDeleteProject } from '@/hooks/useProjects';
import { usePipelineStatuses } from '@/hooks/usePipelines';
import { AddJobToProjectDialog } from '@/components/projects/AddJobToProjectDialog';
import { AdminOnly } from '@/components/AdminOnly';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatMoney } = useCompanyLocale();

  const { data: project, isLoading } = useProject(id ?? null);
  const { data: statuses } = usePipelineStatuses(project?.pipeline_id ?? null);
  const updateProject = useUpdateProject();
  const updateStatus = useUpdateProjectStatus();
  const removeJob = useRemoveJobFromProject();
  const deleteProject = useDeleteProject();

  const [addJobOpen, setAddJobOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [notesDirty, setNotesDirty] = useState(false);
  // Inline rename of the project title
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-4 max-w-3xl">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Project not found.</p>
          <Button variant="link" onClick={() => navigate('/projects')}>← Back to Projects</Button>
        </div>
      </Layout>
    );
  }

  const currentNotes = notesDirty ? notes : (project.notes ?? '');
  const existingJobIds = project.project_jobs.map(pj => pj.job_id);
  const totalRevenue = project.project_jobs.reduce((sum, pj) => sum + (pj.job?.invoice_total ?? 0), 0);

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        {/* Back + title */}
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="mt-0.5">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            {renaming ? (
              <form
                className="flex items-center gap-2"
                onSubmit={e => {
                  e.preventDefault();
                  const next = nameDraft.trim();
                  if (!next || next === project.name) { setRenaming(false); return; }
                  updateProject.mutate(
                    { id: project.id, updates: { name: next } },
                    { onSuccess: () => setRenaming(false) },
                  );
                }}
              >
                <Input
                  autoFocus
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setRenaming(false); }}
                  className="h-9 text-lg font-semibold"
                  maxLength={120}
                />
                <Button type="submit" size="icon" className="h-9 w-9 shrink-0" disabled={updateProject.isPending || !nameDraft.trim()} title="Save name">
                  {updateProject.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => setRenaming(false)} title="Cancel">
                  <X className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-2xl font-bold truncate">{project.name}</h1>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 text-muted-foreground"
                  onClick={() => { setNameDraft(project.name); setRenaming(true); }}
                  title="Rename project"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}
            <p className="text-sm text-muted-foreground">Project detail</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive flex-shrink-0"
            onClick={() => setDeleteConfirm(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>

        {/* Status + info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pipeline Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              value={project.current_status_id ?? ''}
              onValueChange={statusId => updateStatus.mutate({ id: project.id, statusId })}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select stage..." />
              </SelectTrigger>
              <SelectContent>
                {statuses?.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                      {s.is_terminal && <Badge variant="secondary" className="text-[10px] ml-1">Final</Badge>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={currentNotes}
              onChange={e => { setNotes(e.target.value); setNotesDirty(true); }}
              rows={4}
              placeholder="Add notes about this project..."
            />
            {notesDirty && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => {
                    updateProject.mutate(
                      { id: project.id, updates: { notes: currentNotes } },
                      { onSuccess: () => { setNotesDirty(false); setNotes(currentNotes); } }
                    );
                  }}
                  disabled={updateProject.isPending}
                >
                  {updateProject.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  Save Notes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked jobs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Linked Jobs
                {project.project_jobs.length > 0 && (
                  <Badge variant="secondary">{project.project_jobs.length}</Badge>
                )}
              </CardTitle>
              {totalRevenue > 0 && (
                <AdminOnly>
                  <span className="text-sm text-success font-semibold">{formatMoney(totalRevenue)} total</span>
                </AdminOnly>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {project.project_jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No jobs linked yet. Add a job to start tracking.
              </p>
            ) : (
              project.project_jobs.map(pj => (
                <div
                  key={pj.id}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono font-medium">{pj.job?.job_number}</p>
                    <CustomerLink
                      customerId={pj.job?.customer_id}
                      name={pj.job?.customer_name}
                      className="block truncate text-xs text-muted-foreground"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                      {pj.job?.invoice_total != null && pj.job.invoice_total > 0 && (
                      <span className="text-xs text-success font-medium">
                        {formatMoney(pj.job.invoice_total)}
                      </span>
                    )}
                    <Badge variant="outline" className="text-xs capitalize">
                      {pj.job?.status?.replace(/_/g, ' ')}
                    </Badge>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                    >
                      <Link to={`/jobs/${pj.job_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeJob.mutate({ projectId: project.id, jobId: pj.job_id })}
                      disabled={removeJob.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}

            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={() => setAddJobOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Job
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Add job dialog */}
      <AddJobToProjectDialog
        open={addJobOpen}
        onOpenChange={setAddJobOpen}
        projectId={project.id}
        existingJobIds={existingJobIds}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{project.name}" and remove all linked jobs from this project. The jobs themselves will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                deleteProject.mutate(project.id, {
                  onSuccess: () => navigate('/projects'),
                });
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
